import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Printer, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';

const RulesContent = () => {
  const [expandAll, setExpandAll] = useState(false);

  const handlePrint = () => {
    toast.info('Preparing print view...');
    setTimeout(() => window.print(), 100);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <div>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 p-8 rounded-[calc(var(--radius)+6px)] bg-white border hero-gradient relative overflow-hidden"
      >
        <div className="relative z-10">
          <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3">
            Music Game Rules
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
            A competitive music trivia game where teams test their knowledge of popular songs across decades.
            Challenge your friends to identify artists and song titles in 45 seconds!
          </p>
        </div>
      </motion.div>

      {/* Print Controls */}
      <div className="flex gap-3 mb-6" data-testid="print-controls">
        <Button
          onClick={handlePrint}
          variant="outline"
          data-testid="print-rules-button"
          className="rounded-xl"
        >
          <Printer className="mr-2 h-4 w-4" />
          Print Rules
        </Button>
        <Button
          onClick={() => setExpandAll(!expandAll)}
          variant="outline"
          data-testid="expand-all-accordions-button"
          className="rounded-xl"
        >
          {expandAll ? (
            <ChevronUp className="mr-2 h-4 w-4" />
          ) : (
            <ChevronDown className="mr-2 h-4 w-4" />
          )}
          {expandAll ? 'Collapse All' : 'Expand All'}
        </Button>
      </div>

      {/* Rules Sections */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Overview */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="overview"
            title="Overview"
            kicker="Quick Start"
            expandAll={expandAll}
          >
            <p className="text-sm md:text-base leading-relaxed mb-4">
              Nick's Name That Tune is a music trivia game where teams compete to identify song titles and artists
              from popular music across different decades. Teams earn points by correctly guessing songs within a
              45-second time limit.
            </p>
            <Alert className="bg-[hsl(var(--muted))]">
              <AlertDescription>
                <strong>Game Objective:</strong> Test your knowledge of popular music and score more points than
                your opponents by correctly identifying song titles (4 points) and artists (6 points).
              </AlertDescription>
            </Alert>
          </RuleSection>
        </motion.div>

        {/* Teams & Gameplay */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="teams-gameplay"
            title="Teams & Gameplay"
            kicker="Setup"
            expandAll={expandAll}
          >
            <Accordion type="single" collapsible value={expandAll ? 'teams' : undefined}>
              <AccordionItem value="teams">
                <AccordionTrigger>Team Structure</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                    <li>Teams consist of one or more players, but should be as evenly sized as possible</li>
                    <li>One team is in play at a time</li>
                    <li>
                      The guessing team must hand their phones, laptops, tablets, or any other item that could be
                      used to cheat over to the opposing team
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'decade' : undefined} className="mt-2">
              <AccordionItem value="decade">
                <AccordionTrigger>Decade Selection</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                    <li>The guessing team chooses a decade from which their songs will be chosen</li>
                    <li>Teams can use whatever decade they like, and are not required to choose a new decade for each round</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'rounds' : undefined} className="mt-2">
              <AccordionItem value="rounds">
                <AccordionTrigger>Round Structure</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                    <li>A guessing team's round is up when all 10 songs have been played</li>
                    <li>A round is complete when all teams have completed their 10-song turn</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        {/* Song Selection */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="song-selection"
            title="Song Selection Rules"
            kicker="Critical"
            expandAll={expandAll}
          >
            <Accordion type="single" collapsible value={expandAll ? 'playlist' : undefined}>
              <AccordionItem value="playlist">
                <AccordionTrigger>Creating the Playlist</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                    <li>
                      One of the opposing teams will use their phone to load a playlist of popular music from the
                      chosen decade
                    </li>
                    <li>YouTube and Spotify are great resources for playlists</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'release-date' : undefined} className="mt-2">
              <AccordionItem value="release-date">
                <AccordionTrigger>Song Release Date Rules</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      The song's <strong>original release date</strong> will be used to determine if it counts as
                      being from the requisite decade.
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                      <li>NOT the re-release date</li>
                      <li>NOT the live album release date</li>
                      <li>NOT the anniversary edition release date</li>
                      <li>NOT the production date</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'popularity' : undefined} className="mt-2">
              <AccordionItem value="popularity">
                <AccordionTrigger>Popularity Criteria</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      A song will be considered <strong>popular</strong> if it ever reached the{' '}
                      <Badge variant="secondary" className="font-[var(--font-mono)] text-xs">
                        U.S. Billboard Year-End Hot 100
                      </Badge>{' '}
                      chart, regardless of whether or not it did so in the requisite decade.
                    </p>
                    <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        <strong>Example:</strong> Queen's "Bohemian Rhapsody" was released in 1975, but reached
                        number 2 on the Billboard Top 100 seventeen years later after being featured in Wayne's
                        World (1992). So that song would qualify as a popular song from the 1970's, but not the
                        1990's.
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        {/* Remakes & Covers */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="remakes-covers"
            title="Remakes & Covers"
            kicker="Important"
            expandAll={expandAll}
          >
            <Accordion type="single" collapsible value={expandAll ? 'remakes' : undefined}>
              <AccordionItem value="remakes">
                <AccordionTrigger>Remakes</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      <strong>Remakes</strong> are when a band/artist releases a different version of their own
                      song. Remakes will be judged as though they are <strong>THE</strong> original song.
                    </p>
                    <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        <strong>Example:</strong> Metallica released their Live Shit: Binge and Purge box set in
                        1993. It contained live concert recordings of their songs, including their 1988 hit
                        Harvester of Sorrow. If the live version of the song is playing, then the guessing team
                        would need to treat it as though it were the original 1988 version.
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'covers' : undefined} className="mt-2">
              <AccordionItem value="covers">
                <AccordionTrigger>Covers</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      <strong>Covers</strong> are when a band/artist releases their version of somebody else's
                      song. Covers will be judged as though they are <strong>AN</strong> original song.
                    </p>
                    <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        <strong>Example:</strong> In 2006, Rascal Flatts covered Tom Cochrane's 1991 hit "Life is a
                        Highway". If the Rascal Flatts version is playing, then the guessing team would need to
                        treat it as though Rascal Flatts was the original artist (even though the Rascal Flatts
                        version is a nearly identical ripoff of Cochrane's original song).
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        {/* Guessing Rules */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="guessing-rules"
            title="Guessing Rules"
            kicker="45 Seconds"
            expandAll={expandAll}
          >
            <Accordion type="single" collapsible value={expandAll ? 'timer' : undefined}>
              <AccordionItem value="timer">
                <AccordionTrigger>The 45-Second Timer</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                      <li>
                        The guessing team will have <strong>45 seconds</strong> from the beginning of the song to
                        guess the song's title and artist(s)
                      </li>
                      <li>The guessing team must answer before the 45 seconds is up</li>
                      <li>
                        When a track has dialogue or some similar non-musical artifact at the beginning, the timer
                        will not start until the music actually begins
                      </li>
                    </ul>
                    <Alert className="bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        <strong>Why 45 seconds?</strong> The timer was carefully chosen because most popular songs
                        follow a similar structural pattern and fall within a narrow tempo range. The majority of
                        music gets to the chorus at roughly the same time - and if the lyrics mention the song's
                        title, it's usually in the chorus. 45 seconds may or may not be enough time to get that far
                        into the chorus.
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        <Separator className="ticket-divider my-8" />

        {/* Scoring */}
        <motion.div variants={itemVariants}>
          <RuleSection id="scoring" title="Scoring System" kicker="Points" expandAll={expandAll}>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="border-l-4 border-l-[hsl(var(--accent))]">
                  <CardHeader>
                    <CardTitle className="text-lg">Song Title</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-[var(--font-display)] text-[hsl(var(--accent))]">
                      4 Points
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-[hsl(var(--secondary))]">
                  <CardHeader>
                    <CardTitle className="text-lg">Artist(s)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-[var(--font-display)] text-[hsl(var(--secondary))]">
                      6 Points
                    </div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
                Points are not deducted for incorrect answers, unless the answers are part of a challenge.
              </p>
            </div>
          </RuleSection>
        </motion.div>

        {/* Challenges */}
        <motion.div variants={itemVariants}>
          <RuleSection id="challenges" title="Challenges" kicker="Advanced" expandAll={expandAll}>
            <p className="text-sm md:text-base leading-relaxed mb-4">
              Playlists are, more often than not, assembled by people who are not exactly musical historians.
              Consequently, songs may find themselves in a playlist for a decade in which they were not released,
              and/or with the incorrect song title and/or the incorrect artist. The challenges are designed to give
              players the opportunity to object to such occurrences.
            </p>

            <Accordion type="single" collapsible value={expandAll ? 'decade-challenge' : undefined}>
              <AccordionItem value="decade-challenge">
                <AccordionTrigger>Requisite Decade Challenge</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      If the guessing team believes that a song chosen by the opposing team is not originally from
                      the requisite decade they can issue a challenge <strong>before</strong> they guess the
                      artist(s) or song title and before the 45 seconds is up.
                    </p>
                    <p className="text-sm md:text-base leading-relaxed">
                      Wikipedia will be used to find the original release date. If Wikipedia doesn't have that
                      information, then another trusted website can be used (the band's official site, the label's
                      website, etc.).
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'title-challenge' : undefined} className="mt-2">
              <AccordionItem value="title-challenge">
                <AccordionTrigger>Artist or Song Title Challenge</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      After the guessing team guesses incorrectly (and before the next song is played) they will
                      have the opportunity to challenge the opposing team's assertion of the song's title and/or
                      artist(s).
                    </p>
                    <Alert className="bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        <strong>Important:</strong> In order for the challenge to be successful, the title and/or
                        artist(s) from their original guess must be correct. Meaning that even if they are correct
                        that the opposing team got the title/artist wrong, the guessing team could still fail the
                        challenge if they themselves were also wrong in their original answer.
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'popularity-challenge' : undefined} className="mt-2">
              <AccordionItem value="popularity-challenge">
                <AccordionTrigger>Popularity Challenge</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      If the guessing team believes that a song chosen by the opposing team was never popular, then
                      they can issue a challenge at any point before the turn is up and the next song is played.
                    </p>
                    <p className="text-sm md:text-base leading-relaxed">
                      The Billboard Year-End Hot 100 charts will be used to settle popularity challenges.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'challenge-scoring' : undefined} className="mt-2">
              <AccordionItem value="challenge-scoring">
                <AccordionTrigger>Challenge Scoring</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] p-4 rounded-lg">
                      <p className="font-semibold mb-2">Successful Challenge:</p>
                      <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>The guessing team is awarded full points (4 + 6 = 10 points)</li>
                        <li>They get to go again for that round as though that song had never been played</li>
                        <li>Essentially, they get an extra turn</li>
                      </ul>
                    </div>
                    <div className="bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] p-4 rounded-lg">
                      <p className="font-semibold mb-2">Failed Challenge:</p>
                      <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>The guessing team has the full points subtracted from their total score (-10 points)</li>
                        <li>Negative total scores are possible</li>
                        <li>They lose that turn</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        {/* Tie-Breaking */}
        <motion.div variants={itemVariants}>
          <RuleSection id="tie-breaking" title="Tie-Breaking" kicker="Overtime" expandAll={expandAll}>
            <Accordion type="single" collapsible value={expandAll ? 'tiebreaker' : undefined}>
              <AccordionItem value="tiebreaker">
                <AccordionTrigger>Tie-Breaking Rules</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      In the event that two or more teams have the same score, a tie-breaking round will need to be
                      played.
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-sm md:text-base leading-relaxed">
                      <li>Both teams will be guessing at the same time</li>
                      <li>A third team will choose popular music from any year they want</li>
                      <li>The guessing teams will have 20 seconds to guess the song title and artist</li>
                      <li>Any 20 second clip of the song can be played</li>
                      <li>
                        No challenges can be issued during tie-breaking. The third team is judge, jury, and
                        executioner.
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-[hsl(var(--muted))] rounded-lg">
                      <p className="font-semibold mb-2">Tie-Breaking Scoring:</p>
                      <ul className="list-disc pl-6 space-y-1 text-sm md:text-base leading-relaxed">
                        <li>First team to guess before time is up gets 2 points for correct song title</li>
                        <li>First team to guess before time is up gets 3 points for correct artist</li>
                        <li>
                          Points can be split between teams (Team A gets title = 2 points, Team B gets artist = 3
                          points)
                        </li>
                        <li>The winner after 10 rounds wins the game</li>
                        <li>Repeat if the score is still tied</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        <Separator className="ticket-divider my-8" />

        {/* Color of the Game */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="imperfections"
            title="Color of the Game"
            kicker="Philosophy"
            expandAll={expandAll}
          >
            <div className="space-y-4">
              <Alert className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <AlertDescription>
                  <strong>Core Principle:</strong> Imperfections should not be penalized.
                </AlertDescription>
              </Alert>

              <Accordion type="single" collapsible value={expandAll ? 'imperfections' : undefined}>
                <AccordionItem value="imperfections">
                  <AccordionTrigger>What Counts as an Imperfection?</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      <p className="text-sm md:text-base leading-relaxed">
                        The point of the game is to showcase your ability to recognize and recall popular music
                        better than your opponents, not prove your superior pronoun or preposition memorization
                        skills.
                      </p>
                      <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))]">
                        <AlertDescription>
                          <strong>Example:</strong> If Bonnie Tyler's "Total Eclipse of the Heart" is the song, and
                          the team guesses "Bonnie Tyler Total Eclipse of <em>My</em> Heart", they would still get
                          the points.
                        </AlertDescription>
                      </Alert>
                      <p className="text-sm md:text-base leading-relaxed">
                        Same for when a song has a parenthetical statement as part of the song title.
                      </p>
                      <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))]">
                        <AlertDescription>
                          <strong>Example:</strong> Bryan Adams' "Everything I Do (I Do it for You)" - both versions
                          with and without the parenthetical are acceptable.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Accordion type="single" collapsible value={expandAll ? 'collaborators' : undefined} className="mt-2">
                <AccordionItem value="collaborators">
                  <AccordionTrigger>Collaborators & Featured Artists</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      <p className="text-sm md:text-base leading-relaxed">
                        A team is not required to (nor are they rewarded for) naming collaborators, so long as the
                        primary artist is part of the answer.
                      </p>
                      <div className="mt-4 p-4 bg-[hsl(var(--muted))] rounded-lg">
                        <p className="font-semibold mb-2">
                          Example: "Love the Way You Lie" by Eminem featuring Rihanna
                        </p>
                        <ul className="list-disc pl-6 space-y-1 text-sm md:text-base leading-relaxed">
                          <li>Guessed Eminem and Rihanna = Full points ✓</li>
                          <li>Guessed Eminem, but not Rihanna = Full points ✓</li>
                          <li>Guessed Rihanna, but not Eminem = No points ✗</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Accordion type="single" collapsible value={expandAll ? 'disputes' : undefined} className="mt-2">
                <AccordionItem value="disputes">
                  <AccordionTrigger>Dispute Resolution</AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm md:text-base leading-relaxed">
                      If there is doubt as to whether or not something counts as an imperfection or an incorrect
                      answer, then a <strong>majority vote of all people present</strong> will determine if the team
                      is awarded the points. This could be used as an offensive strategy, though one would need to
                      exercise caution when doing so. In the event of a tie vote, the turn is repeated and a new
                      song is chosen from the playlist.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </RuleSection>
        </motion.div>

        {/* Playlist Accuracy */}
        <motion.div variants={itemVariants}>
          <RuleSection
            id="playlist-accuracy"
            title="Playlist Accuracy & Standards"
            kicker="Guidelines"
            expandAll={expandAll}
          >
            <Accordion type="single" collapsible value={expandAll ? 'standards' : undefined}>
              <AccordionItem value="standards">
                <AccordionTrigger>Popularity Standards</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm md:text-base leading-relaxed">
                    The popularity standards are up for debate, but <strong>only until the game starts</strong>. We
                    should settle on a standard that doesn't lose sight of the point of the game: to test your
                    knowledge of songs we've all heard, not some random songs that nobody ever listens to.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'timer-choice' : undefined} className="mt-2">
              <AccordionItem value="timer-choice">
                <AccordionTrigger>Why 45 Seconds?</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-sm md:text-base leading-relaxed">
                      The 45 second timer was carefully chosen and <strong>should not be changed</strong>.
                    </p>
                    <Alert className="bg-[hsl(var(--muted))]">
                      <AlertDescription>
                        If you ever take a musical theory class, you learn that most popular songs follow a similar
                        structural pattern and fall within a narrow tempo range. Because of this, the majority of
                        music we listen to gets to the chorus at roughly the same time. If the lyrics of a song
                        mention the song's title, then it's usually in the chorus. However, 45 seconds may or may
                        not be enough time to get that far into the chorus.
                      </AlertDescription>
                    </Alert>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible value={expandAll ? 'challenge-penalty' : undefined} className="mt-2">
              <AccordionItem value="challenge-penalty">
                <AccordionTrigger>Challenge Penalty Rationale</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm md:text-base leading-relaxed">
                    Point subtractions are used for failed challenges to discourage players from abusing the
                    challenges.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>

        {/* EDM Problem */}
        <motion.div variants={itemVariants}>
          <RuleSection id="edm-problem" title="The EDM Problem" kicker="Exclusion" expandAll={expandAll}>
            <Alert className="bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] mb-4">
              <AlertDescription>
                <strong>Important:</strong> EDM is not recommended for this game.
              </AlertDescription>
            </Alert>

            <Accordion type="single" collapsible value={expandAll ? 'edm-reasons' : undefined}>
              <AccordionItem value="edm-reasons">
                <AccordionTrigger>Why EDM Doesn't Work</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">1. Collaboration Confusion</h4>
                      <p className="text-sm md:text-base leading-relaxed">
                        The digital nature of EDM has made collaboration incredibly easy. As such, different people
                        view the exact same song as being by different artists.
                      </p>
                      <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))] mt-2">
                        <AlertDescription>
                          <strong>Example:</strong> Some see "Hello" as being by Martin Solveig, while others would
                          argue that it's a song by Dragonette.
                        </AlertDescription>
                      </Alert>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">2. Sampling Issues</h4>
                      <p className="text-sm md:text-base leading-relaxed">
                        A lot of EDM is heavily sampled from other songs. It would sometimes be incredibly difficult
                        to accurately judge a song's original artist, title, or popularity.
                      </p>
                      <Alert className="border-l-4 border-l-[hsl(var(--secondary))] bg-[hsl(var(--muted))] mt-2">
                        <AlertDescription>
                          <strong>Example:</strong> The Amen Break - a widely sampled drum break that appears in
                          countless EDM tracks.
                        </AlertDescription>
                      </Alert>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">3. Genre Boundary Issues</h4>
                      <p className="text-sm md:text-base leading-relaxed">
                        The lines that distinguish between popular EDM and other genres were...
                      </p>
                      <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base leading-relaxed">
                        <li>...blurry in the 70s</li>
                        <li>...clearly defined in the 90s</li>
                        <li>...and blurry again in the 2010s</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </RuleSection>
        </motion.div>
      </motion.div>
    </div>
  );
};

const RuleSection = ({ id, title, kicker, children, expandAll }) => {
  return (
    <Card
      id={id}
      data-testid={`rules-section-${id}`}
      className="section-card scroll-mt-24 rounded-[calc(var(--radius)+6px)] border bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]"
    >
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 rounded-full bg-[hsl(var(--secondary))]" />
            <Badge variant="secondary" className="font-[var(--font-mono)] text-xs">
              {kicker}
            </Badge>
          </div>
        </div>
        <CardTitle className="text-2xl md:text-3xl font-[var(--font-display)] tracking-tight mt-2">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

export default RulesContent;
